import { getIn } from '@aytm/app/stats/lib/util/helpers'

import { processText } from '@aytm/app/stats/page/components/lib/utils'
import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { getImage } from '@aytm/app/stats/lib/stat-page'
import { applySorted } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

const calcTableItemsListProps = ({ block, format, answer, percentDecimals }) => {
  const isLoading = 'loading' === answer

  const cols = [
    {
      type: 'title',
      width: `${100 / (block.table.length + 1)}%`,
      title: block.title || ''
    }
  ].concat(
    block.table.map(b => {
      return {
        type: 'value',
        title: b.title || '',
        width: `${100 / (block.table.length + 1)}%`
      }
    })
  )

  const rows = block.answerVariants.map(av => {
    return {
      cells: [
        {
          title: av.text || ''
        }
      ].concat(
        block.table.map(b => {
          const a = Object.hasOwn(b, 'path')
            ? getIn(b.path, answer, {
                $key: av.position
              })
            : undefined
          const classNames =
            (Object.hasOwn(b, 'cellStylePath') &&
              getIn(b.cellStylePath, answer, {
                $key: av.position
              })) ||
            []
          const cell = {
            value: '',
            loading: isLoading,
            classNames: (Array.isArray(classNames) && classNames) || classNames.split(' ')
          }
          // loading sentinel / missing data → keep the empty cell instead of NaN or a crash
          if (a != null && typeof a !== 'object') {
            switch (b.type) {
              case 'float':
                cell.value = parseFloat(a).toFixed((format != null ? format.decimals : void 0) || 1)
                break
              case 'integer':
                cell.value = parseInt(a, 10)
                break
              case 'percents':
                cell.value = `${a.toFixed(percentDecimals ?? 1)}%`
                break
              default:
                cell.value = a
            }
          }
          return cell
        })
      ),
      position: av.position
    }
  })

  return { cols, rows }
}

const calcAnswerVariantsItemsListProps = ({ block, format, answer, sortOrder, onSort, percentDecimals }) => {
  const hideTotal = undefined != format && Object.hasOwn(format || {}, 'hideTotal') && format.hideTotal == true
  const isLoading = 'loading' === answer

  const cols = [
    {
      type: 'title',
      title: block.title || 'Answers'
    }
  ]

  const rows = block.answerVariants.map(av => {
    const a = answer[`a${av.position}`] || {}
    const row = {
      cells: [
        {
          title: processText(av.text),
          label: 'A' + av.position,
          colorIndex: ((av.position - 1) % 21) + 1
        }
      ],
      position: av.position
    }
    if ('raw' === block.chartMode) {
      row.cells.push({
        value: a.text || '',
        loading: isLoading
      })
    } else {
      row.cells.push({
        amount: a.amount || 0,
        percents: a.percents || 0,
        loading: isLoading
      })
    }
    if (Object.hasOwn(av, 'image')) {
      row.cells[0].image = getImage(av.image)
    }
    return row
  })

  // verbatims read in the order they were answered in; there is nothing to rank them by
  if ('raw' === block.chartMode) {
    cols.push({
      type: 'value',
      title: block.responsesTitle || ''
    })

    return { cols, rows }
  }

  cols[0].sort = (aRow, bRow, dir) => dir * (aRow.position - bRow.position)
  cols.push({
    type: 'progress',
    percentDecimals,
    title: `${block.responsesTitle || 'Respondents'}${(!hideTotal && `:<small>${formatAmounts(answer, 'total')}</small>`) || ''}`,
    sort: (aRow, bRow, dir) =>
      dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000))
  })

  return { cols: applySorted(cols, sortOrder), rows, onSort }
}

// A table shaped by the block's own column list ranks nothing: its columns carry no comparator, so
// no header offers a sort and there is no order to remember.
export const calcTableProps = ({ block, format, answer, sortOrder, onSort, percentDecimals }) =>
  Object.hasOwn(block, 'table')
    ? calcTableItemsListProps({ block, format, answer, percentDecimals })
    : calcAnswerVariantsItemsListProps({ block, format, answer, sortOrder, onSort, percentDecimals })
